import React, { useMemo, useState } from "react";
import AssessmentTile from "./AssessmentTile";
import AssessmentChartView from "./AssessmentChartView";
import useAiAssessments from "./useAiAssessments";
import {
  ASSESSMENT_TIMEFRAMES, DEFAULT_ASSESSMENT_FILTERS, coinDataFor, coverageText, validAssessmentFilters,
} from "./assessmentView";
import "./AiAssessments.css";
import { useUserSession } from "../auth/UserSessionContext";

const ASSESSMENT_FILTERS_STORAGE_KEY = "aiAssessmentFilters";

function normalizedStoredFilters(value) {
  const candidate = value && typeof value === "object" ? value : {};
  const threshold = (stored) => stored === "" || stored === null || stored === undefined
    ? ""
    : Number.isFinite(Number(stored)) && Number(stored) >= 0 && Number(stored) <= 100
      ? String(stored)
      : "";
  return {
    side: ["ALL", "LONG", "SHORT"].includes(candidate.side) ? candidate.side : DEFAULT_ASSESSMENT_FILTERS.side,
    timeframe: ["ALL", ...ASSESSMENT_TIMEFRAMES].includes(candidate.timeframe)
      ? candidate.timeframe : DEFAULT_ASSESSMENT_FILTERS.timeframe,
    range: ["all", "1h", "4h", "24h", "7d", "30d"].includes(candidate.range)
      ? candidate.range : DEFAULT_ASSESSMENT_FILTERS.range,
    minConfidence: threshold(candidate.minConfidence),
    minScore: threshold(candidate.minScore),
  };
}

function loadStoredFilters() {
  try {
    return normalizedStoredFilters(JSON.parse(window.localStorage.getItem(ASSESSMENT_FILTERS_STORAGE_KEY)));
  } catch (error) {
    return { ...DEFAULT_ASSESSMENT_FILTERS };
  }
}

function AccessMessage({ status, onRetry }) {
  if (status === "checking") return <div className="assessmentState">Checking Ultimate access…</div>;
  if (status === "signedout") return <div className="assessmentState">Sign in to view AI Assessments.</div>;
  if (status === "locked") return (
    <div className="assessmentState assessmentLocked">
      <strong>AI Assessments requires Ultimate.</strong>
      <span>Your existing History events remain available.</span>
    </div>
  );
  if (status === "unavailable") return (
    <div className="assessmentState">
      <strong>AI Assessment access is temporarily unavailable.</strong>
      <button type="button" className="historyBtn" onClick={onRetry}>Retry</button>
    </div>
  );
  return null;
}

export default function AiAssessmentsTab({ insights, settings, setSettings }) {
  const { status: sessionStatus } = useUserSession();
  const authenticated = sessionStatus === "authenticated";
  const [initialFilters] = useState(loadStoredFilters);
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const filterValid = useMemo(() => validAssessmentFilters(draft), [draft]);
  const feed = useAiAssessments({ authenticated, filters });
  const feedScrollPosition = React.useRef(0);
  const lastOpenedId = React.useRef(null);
  const initialAssessmentId = useMemo(() => new URLSearchParams(window.location.search).get("assessment"), []);
  const initialAssessmentOpened = React.useRef(false);

  React.useEffect(() => {
    if (feed.accessStatus !== "allowed" || !initialAssessmentId || initialAssessmentOpened.current) return;
    initialAssessmentOpened.current = true;
    feed.select(initialAssessmentId);
  }, [feed.accessStatus, feed.select, initialAssessmentId]);

  const selectAssessment = (id) => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", "history");
    url.searchParams.set("historyView", "ai-assessments");
    url.searchParams.set("assessment", id);
    window.history.replaceState({}, "", url);
    feed.select(id);
  };

  const openAssessment = (id) => {
    feedScrollPosition.current = window.scrollY;
    lastOpenedId.current = id;
    selectAssessment(id);
  };

  const closeAssessment = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("assessment");
    window.history.replaceState({}, "", url);
    feed.closeDetail();
    window.requestAnimationFrame(() => {
      window.scrollTo(0, feedScrollPosition.current);
      const selector = `[data-assessment-id="${window.CSS?.escape ? window.CSS.escape(lastOpenedId.current) : lastOpenedId.current}"]`;
      document.querySelector(selector)?.focus();
    });
  };

  const updateFilter = (name, value) => {
    const next = { ...draft, [name]: value };
    setDraft(next);
    if (!validAssessmentFilters(next)) return;
    setFilters(next);
    try {
      window.localStorage.setItem(ASSESSMENT_FILTERS_STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      // Filtering still works when storage is unavailable or full.
    }
  };

  if (feed.accessStatus !== "allowed") return <AccessMessage status={feed.accessStatus} onRetry={feed.retry} />;

  return (
    <section className="assessmentsRoot" aria-label="AI Assessments History">
      {!feed.selectedId && <>
      <div className="assessmentFilters">
        <label>
          Side
          <select value={draft.side} onChange={(event) => updateFilter("side", event.target.value)}>
            <option value="ALL">Long and short</option>
            <option value="LONG">Long</option>
            <option value="SHORT">Short</option>
          </select>
        </label>
        <label>
          Timeframe
          <select value={draft.timeframe}
            onChange={(event) => updateFilter("timeframe", event.target.value)}>
            <option value="ALL">All timeframes</option>
            {ASSESSMENT_TIMEFRAMES.map((timeframe) => (
              <option key={timeframe} value={timeframe}>{timeframe}</option>
            ))}
          </select>
        </label>
        <label>
          Range
          <select value={draft.range} onChange={(event) => updateFilter("range", event.target.value)}>
            <option value="all">All available</option>
            <option value="1h">1 hour</option>
            <option value="4h">4 hours</option>
            <option value="24h">24 hours</option>
            <option value="7d">7 days</option>
            <option value="30d">30 days</option>
          </select>
        </label>
        <label className="assessmentFilterWithHelp">
          Minimum AI confidence
          <input aria-label="Minimum AI confidence" type="number" min="0" max="100" value={draft.minConfidence}
            onChange={(event) => updateFilter("minConfidence", event.target.value)} />
          <small>Model certainty in its assessment; it is not a trade win probability.</small>
        </label>
        <label className="assessmentFilterWithHelp">
          Minimum score
          <input aria-label="Minimum score" type="number" min="0" max="100" value={draft.minScore}
            onChange={(event) => updateFilter("minScore", event.target.value)} />
          <small>Assessments without a score are excluded when a minimum score is set.</small>
        </label>
        <button type="button" className="historyBtn assessmentRefreshButton" onClick={feed.retry}
          title="Refresh assessments" aria-label="Refresh assessments">
          <span className="assessmentRefreshIcon" aria-hidden="true" />
        </button>
        {!filterValid && <span className="assessmentFilterError">Use a value from 0 to 100.</span>}
      </div>

      <p className="assessmentCoverage">{coverageText(feed.coverage)}</p>
      {feed.hasNewResults && (
        <div className="assessmentNewResults">
          New results are available. Your open assessment stayed in place.
          <button type="button" className="historyBtn" onClick={feed.applyNewResults}>Show new results</button>
        </div>
      )}
      {feed.error && (
        <div className="assessmentState assessmentError">
          Could not load AI Assessments. <button type="button" className="historyBtn" onClick={feed.retry}>Retry</button>
        </div>
      )}
      {feed.loading && feed.items.length === 0 && <div className="assessmentState">Loading AI Assessments…</div>}
      {!feed.loading && !feed.error && feed.items.length === 0 && (
        <div className="assessmentState">No accepted or supporting assessments match these filters.</div>
      )}
      </>}
      {feed.selectedId ? <AssessmentChartView
        items={feed.items}
        selectedId={feed.selectedId}
        onSelect={selectAssessment}
        onLoadMore={feed.loadMore}
        hasMore={Boolean(feed.nextCursor)}
        onClose={closeAssessment}
        authenticated={authenticated}
        insights={insights}
        settings={settings}
        setSettings={setSettings}
        onAccessError={feed.protectedError}
      /> : <div className="assessmentGrid">
        {feed.items.map((assessment) => (
          <AssessmentTile key={assessment.id} assessment={assessment}
            coinData={coinDataFor(insights, assessment.symbol)} onOpen={openAssessment} />
        ))}
      </div>}
      {!feed.selectedId && (feed.nextCursor ? (
        <button type="button" className="historyBtn assessmentLoadMore" disabled={feed.loading} onClick={feed.loadMore}>
          {feed.loading ? "Loading…" : "Load more"}
        </button>
      ) : feed.items.length > 0 ? <div className="assessmentEnd">End of available assessments</div> : null)}
    </section>
  );
}
